import { z } from "zod";

/**
 * Contract between the Command Bar's assistant (running on the device, where the data lives) and
 * the Worker that calls Workers AI. The model only sees what these tools return.
 */

export const toolCallSchema = z.object({
  id: z.string().max(200),
  name: z.string().max(100),
  /** JSON-encoded arguments. */
  arguments: z.string().max(20_000)
});
export type ToolCall = z.infer<typeof toolCallSchema>;

export const chatMessageSchema = z.discriminatedUnion("role", [
  z.object({ role: z.literal("system"), content: z.string().max(40_000) }),
  z.object({ role: z.literal("user"), content: z.string().max(4_000) }),
  z.object({ role: z.literal("assistant"), content: z.string().max(20_000), toolCalls: z.array(toolCallSchema).max(12).optional() }),
  z.object({ role: z.literal("tool"), toolCallId: z.string().max(200), content: z.string().max(60_000) })
]);
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const assistantRequestSchema = z.object({ messages: z.array(chatMessageSchema).min(1).max(40) });
export type AssistantRequest = z.infer<typeof assistantRequestSchema>;

export const assistantResponseSchema = z.object({
  content: z.string(),
  toolCalls: z.array(toolCallSchema),
  usage: z.object({ tokens: z.number().int().nonnegative(), monthTokens: z.number().int().nonnegative(), monthCap: z.number().int().nonnegative() })
});
export type AssistantResponse = z.infer<typeof assistantResponseSchema>;

interface ToolDefinition {
  type: "function";
  function: { name: string; description: string; parameters: Record<string, unknown> };
}

const date = { type: "string", description: "Local date, YYYY-MM-DD" };
const time = { type: "string", description: "Local time, HH:MM (24-hour)" };

export const ASSISTANT_TOOLS: ToolDefinition[] = [
  {
    type: "function",
    function: {
      name: "find_entries",
      description: "List time entries between two dates (inclusive), optionally for one project or matching text in the description. Returns at most 100, newest first.",
      parameters: { type: "object", properties: { from: date, to: date, projectId: { type: "string" }, text: { type: "string" } }, required: ["from", "to"] }
    }
  },
  {
    type: "function",
    function: {
      name: "summarize",
      description: "Totals of tracked time and earnings between two dates (inclusive), grouped by project, client, task, or day. Money is reported per currency and never converted.",
      parameters: { type: "object", properties: { from: date, to: date, groupBy: { type: "string", enum: ["project", "client", "task", "day"] } }, required: ["from", "to", "groupBy"] }
    }
  },
  {
    type: "function",
    function: {
      name: "log_time",
      description: "Record a finished time entry on a project.",
      parameters: { type: "object", properties: { projectId: { type: "string" }, taskId: { type: "string" }, date, start: time, end: time, description: { type: "string" } }, required: ["projectId", "date", "start", "end"] }
    }
  },
  {
    type: "function",
    function: {
      name: "start_timer",
      description: "Start the timer now, stopping any running timer.",
      parameters: { type: "object", properties: { projectId: { type: "string" }, description: { type: "string" } } }
    }
  },
  { type: "function", function: { name: "stop_timer", description: "Stop the running timer now.", parameters: { type: "object", properties: {} } } },
  {
    type: "function",
    function: {
      name: "update_entries",
      description: "Change existing entries: move them to another project, change the description, or mark them billable or not. Use ids from find_entries.",
      parameters: { type: "object", properties: { entryIds: { type: "array", items: { type: "string" } }, projectId: { type: "string" }, description: { type: "string" }, billable: { type: "boolean" } }, required: ["entryIds"] }
    }
  },
  {
    type: "function",
    function: {
      name: "list_invoices",
      description: "List invoices, newest first, optionally by status or client. unpaid means sent and not paid; overdue means unpaid past the due date. Totals include tax.",
      parameters: { type: "object", properties: { status: { type: "string", enum: ["draft", "sent", "paid", "unpaid", "overdue"] }, clientId: { type: "string" } } }
    }
  },
  {
    type: "function",
    function: {
      name: "uninvoiced_work",
      description: "Earned money not yet on a sent invoice, per client and currency, with the hours and the oldest uninvoiced day.",
      parameters: { type: "object", properties: { clientId: { type: "string" } } }
    }
  },
  {
    type: "function",
    function: {
      name: "draft_invoice",
      description: "Create a draft invoice for a client from all uninvoiced billable time in a period (inclusive), plus finished fixed price items. Default period: the previous calendar month. Currency is needed only when the client has several.",
      parameters: { type: "object", properties: { clientId: { type: "string" }, from: date, to: date, currency: { type: "string" } }, required: ["clientId"] }
    }
  },
  {
    type: "function",
    function: {
      name: "split_invoice",
      description: "Split a draft invoice into 2 to 5 installments, each its own invoice billing a share of the whole, as whole percentages adding to 100 (\"50/50\" is [50, 50]). Also changes the shares of a split; [100] merges it back. Sent installments keep their share. Use ids from list_invoices or draft_invoice.",
      parameters: { type: "object", properties: { invoiceId: { type: "string" }, percents: { type: "array", items: { type: "integer" } } }, required: ["invoiceId", "percents"] }
    }
  },
  {
    type: "function",
    function: {
      name: "mark_invoice_sent",
      description: "Mark a draft invoice as sent: it gets its number and its entries lock. Use ids from list_invoices.",
      parameters: { type: "object", properties: { invoiceId: { type: "string" }, issueDate: date }, required: ["invoiceId"] }
    }
  },
  {
    type: "function",
    function: {
      name: "mark_invoice_paid",
      description: "Mark a sent invoice as paid in full. Use ids from list_invoices.",
      parameters: { type: "object", properties: { invoiceId: { type: "string" }, paidDate: date }, required: ["invoiceId"] }
    }
  },
  {
    type: "function",
    function: {
      name: "find_events",
      description: "List planned calendar events between two dates (inclusive), with how much time was logged from each. Use it for questions about plans, schedule, or how much of the plan was done.",
      parameters: { type: "object", properties: { from: date, to: date }, required: ["from", "to"] }
    }
  },
  {
    type: "function",
    function: {
      name: "list_tasks",
      description: "List open (pending, not done) Tasks, optionally for one project, with time logged and the estimate. Projects closest to their deadline come first.",
      parameters: { type: "object", properties: { projectId: { type: "string" } } }
    }
  },
  {
    type: "function",
    function: {
      name: "plan_event",
      description: "Put a planned event on the calendar. It never counts as tracked time. Without a start time it is all day (endDate makes it span several days). Default length is one hour.",
      parameters: {
        type: "object",
        properties: {
          title: { type: "string" }, date, start: time, end: time, endDate: date, projectId: { type: "string" },
          repeat: { type: "string", enum: ["daily", "weekdays", "weekly", "monthly"] }, repeatCount: { type: "integer", description: "Stop after this many occurrences" },
          notes: { type: "string" }
        },
        required: ["title", "date"]
      }
    }
  },
  {
    type: "function",
    function: {
      name: "delete_entries",
      description: "Move entries to Trash. Use ids from find_entries.",
      parameters: { type: "object", properties: { entryIds: { type: "array", items: { type: "string" } } }, required: ["entryIds"] }
    }
  }
];

export interface AssistantContext {
  /** e.g. "Tuesday, 2026-10-20 15:12" */
  localNow: string;
  timeZone: string;
  projects: Array<{ id: string; name: string; client: string | null; billing: string; currency: string; tasks: Array<{ id: string; name: string }> }>;
  clients: Array<{ id: string; name: string; currencies: string[] }>;
  running: string | null;
}

export function buildSystemPrompt(context: AssistantContext): string {
  return [
    "You are the assistant inside ontrack, a personal time and earnings tracker. You act through tools on the user's own data.",
    `Now: ${context.localNow} (${context.timeZone}). Interpret "today", "yesterday", weekdays, and times in this time zone.`,
    "Reply in the language the user wrote in (English or Spanish). Be brief: one or two sentences.",
    "The app shows the entries, totals, invoices, unbilled work, tasks, and events your tools return as cards under your reply. Do not list them again: lead with the answer itself (the figure, the name, the conclusion) and add only what the records do not say.",
    "Write plain text only. No Markdown: no asterisks, no headings, no tables. Use a new line with a hyphen for list items.",
    "Use only project, task, and entry ids that appear below or in tool results. If the project is ambiguous, ask instead of guessing.",
    "Money belongs to a currency. Never convert or add amounts in different currencies.",
    "For questions, call find_entries or summarize first, then answer from the results. For changes, call the change tools, then confirm what you did in one sentence.",
    "Invoices: answer questions about invoices, unpaid or overdue money with list_invoices, and about unbilled work with uninvoiced_work. To bill a client (\"invoice Acme for October\", \"factura a Acme\"), call draft_invoice. To charge in parts (\"bill Acme 50% now and 50% later\", \"en dos cuotas\"), call split_invoice on the draft. Installments are sent in order. Invoice ids come from list_invoices or draft_invoice.",
    "Tasks: open tasks are the user's pending work. For pending, open, or remaining tasks or to-dos (\"what's left on Acme\", \"tareas pendientes\"), call list_tasks. Pending tasks are work to do, not money to bill.",
    "Calendar: plans are events, separate from tracked time. To plan or schedule something (\"plan a review with Acme tomorrow at 10\", \"agenda una reunión el lunes\"), call plan_event. For questions about plans versus what was done, call find_events; it includes Google Calendar events (marked with googleCalendar). You can't change or delete Google Calendar events: point the User to the Calendar for that.",
    context.running ? `Running timer: ${context.running}.` : "No timer is running.",
    `Projects (JSON): ${JSON.stringify(context.projects)}`,
    `Clients (JSON): ${JSON.stringify(context.clients)}`
  ].join("\n");
}
